import React, { useState } from "react";
import { PiPlus } from "react-icons/pi";
import { COUNTRY_LIST } from "../../utils/constant";
import { toast } from "react-toastify";
import axiosUserInstance from "../../axiosUserInstance";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "react-tooltip";
import "../../Assets/css/Form.css";

const FormWidgetModal = ({
  modalForm,
  saveModal,
  setModalForm,
  widgetId,
  onSaveFormFields,
  onUpdateFormWidget,
  isPublic,
  userName,
  onSubmitForm,
  closeModal,
  userData,
}) => {
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const handleSaveFormFields = async () => {
    try {
      if (!widgetId) {
        toast.error(
          "Form widget ID is not available. Please create the form widget first.",
        );
        return;
      }

      // Prepare the form data
      const formData = {
        requiredFields: modalForm?.requiredFields || [],
        removedFields: modalForm?.removedFields || [],
      };

      // Validation: Check if all fields are removed
      const allFields = ["name", "email", "number", "dob", "country"];
      const allRemoved = allFields.every((field) =>
        formData.removedFields.includes(field),
      );

      if (allRemoved) {
        toast.error(
          "At least one field must remain. You cannot remove all fields.",
        );
        return;
      }

      setLoading(true);

      // Call the API to save form fields
      const response = await axiosUserInstance.post(
        "/widget/update-form-fields",
        {
          widgetId,
          formData,
        },
      );

      if (response?.status === 200) {
        toast.success("Form fields updated successfully");
        // Update parent with the new widget data
        if (onUpdateFormWidget) {
          onUpdateFormWidget(response.data?.data);
        }
        // Call the parent callback if provided
        if (onSaveFormFields) {
          onSaveFormFields(response.data?.data);
        }
        // Also call the original saveModal if provided
        if (saveModal) {
          saveModal();
        }
      } else {
        toast.error(response?.data?.message || "Failed to save form fields");
      }
    } catch (error) {
      console.error("Error saving form fields:", error);
      toast.error(
        error?.response?.data?.message || "Failed to save form fields",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitPublicForm = async () => {
    try {
      // if (!widgetId || !userName) {
      //   toast.error("Invalid form data");
      //   return;
      // }

      // Collect form data
      const formData = {
        name: modalForm?.name || "",
        email: modalForm?.email || "",
        number: modalForm?.number || "",
        dob: modalForm?.dob || "",
        country: modalForm?.country || "",
      };

      // defensive guard
      if (!userName || !widgetId) {
        console.warn("submit attempt without username/widgetId", {
          userName,
          widgetId,
        });
        toast.error("Unable to submit form, missing context");
        return;
      }

      // Client-side validation: check if required fields exist and are populated
      if (modalForm?.requiredFields && modalForm.requiredFields.length > 0) {
        const errors = {};
        for (const field of modalForm.requiredFields) {
          if (!formData[field] || formData[field].trim() === "") {
            errors[field] = true;
          }
        }
        if (Object.keys(errors).length > 0) {
          setFieldErrors(errors);
          return;
        }
      }
      setFieldErrors({});

      setLoading(true);

      // Submit form to backend
      const response = await axiosUserInstance.post("/user/form/submit", {
        userName,
        widgetId,
        formData,
      });

      if (response?.status === 200 || response?.status === 201) {
        toast.success("Form submitted successfully!");
        // Preserve submitted values (use returned formData when available)
        const saved = response.data?.data?.formData || formData;
        setModalForm((f) => ({ ...f, ...saved }));
        // Call parent callback if provided
        if (onSubmitForm) {
          onSubmitForm(response.data?.data);
        }
      } else {
        toast.error(response?.data?.message || "Failed to submit form");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      const msg = error?.response?.data?.message || "Failed to submit form";
      toast.error(msg);
      if (msg === "Form widget not found") {
        // reset and notify parent so it can dismiss modal
        setModalForm({ name: "", email: "", number: "", dob: "", country: "" });
        if (onSubmitForm) onSubmitForm(null);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="">
      {isPublic ? (
        <>
          <button
            className="form-close"
            onClick={closeModal}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>

          <div className="d-flex align-items-center gap-3">
            <h3 className="modal-title">Stay tuned to {userData?.fullName}</h3>
            <img
              src={userData?.image}
              alt="profile image"
              className="form-public-profile-img"
            />
          </div>
          <p className="text-secondary">
            Welcome, please fill in the form to be notfied and subscribe to be
            part of my email newsletter
          </p>
        </>
      ) : (
        <>
          <h3 className="modal-title">User Collection Form</h3>
          <p className="text-secondary">
            Pick fields you need - add, edit and delete.
          </p>
        </>
      )}

      <>
        <div className="form-group">
          {!isPublic && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 32,
                marginBottom: 4,
              }}
            >
              <span style={{ fontSize: 12, color: "#6b7280" }}>Required</span>
              <span style={{ fontSize: 12, color: "#6b7280" }}>Remove</span>
            </div>
          )}

          {/* Name Field */}
          {!(isPublic && modalForm?.removedFields?.includes("name")) && (
            <>
              <label className="form-label">Name</label>

              <div className="form-group form-widget-field">
                <div style={{ flex: 1 }} className="form-input">
                  <input
                    className="form-control"
                    value={modalForm?.name || ""}
                    onChange={(e) => {
                      setModalForm((f) => ({
                        ...f,
                        name: e.target.value,
                      }));
                      setFieldErrors((prev) => ({ ...prev, name: false }));
                    }}
                    disabled={!isPublic}
                    placeholder="Enter a name"
                    style={{
                      borderColor: fieldErrors?.name ? "#dc3545" : "",
                      borderRadius:
                        typeof userData?.formTheme?.borderRadius === "number"
                          ? userData?.formTheme?.borderRadius
                          : undefined,
                    }}
                  />
                  {isPublic && fieldErrors?.name && (
                    <small
                      style={{
                        color: "#dc3545",
                        display: "block",
                        marginTop: 4,
                      }}
                    >
                      Name is required
                    </small>
                  )}
                </div>
                {!isPublic && (
                  <>
                    {/* Required Checkbox */}
                    <input
                      type="checkbox"
                      checked={
                        modalForm?.requiredFields?.includes("name") || false
                      }
                      onChange={(e) =>
                        setModalForm((f) => ({
                          ...f,
                          requiredFields: e.target.checked
                            ? [...(f.requiredFields || []), "name"]
                            : (f.requiredFields || []).filter(
                                (field) => field !== "name"
                              ),
                        }))
                      }
                      style={{
                        width: 18,
                        height: 18,
                        cursor: "pointer",
                      }}
                    />
                    {/* Remove Checkbox */}
                    <input
                      type="checkbox"
                      checked={
                        modalForm?.removedFields?.includes("name") || false
                      }
                      onChange={(e) =>
                        setModalForm((f) => ({
                          ...f,
                          removedFields: e.target.checked
                            ? [...(f.removedFields || []), "name"]
                            : (f.removedFields || []).filter(
                                (field) => field !== "name"
                              ),
                        }))
                      }
                      style={{
                        width: 18,
                        height: 18,
                        cursor: "pointer",
                      }}
                    />
                  </>
                )}
              </div>
            </>
          )}

          {/* Email Address Field */}
          {!(isPublic && modalForm?.removedFields?.includes("email")) && (
            <>
              <label className="form-label">Email Address</label>

              <div className="form-group form-widget-field">
                <div style={{ flex: 1 }}>
                  <input
                    className="form-control"
                    value={modalForm?.email || ""}
                    onChange={(e) => {
                      setModalForm((f) => ({
                        ...f,
                        email: e.target.value,
                      }));
                      setFieldErrors((prev) => ({ ...prev, email: false }));
                    }}
                    disabled={!isPublic}
                    placeholder="Enter a email"
                    style={{
                      borderColor: fieldErrors?.email ? "#dc3545" : "",
                      borderRadius:
                        typeof userData?.formTheme?.borderRadius === "number"
                          ? userData?.formTheme.borderRadius
                          : undefined,
                    }}
                  />
                  {isPublic && fieldErrors?.email && (
                    <small
                      style={{
                        color: "#dc3545",
                        display: "block",
                        marginTop: 4,
                      }}
                    >
                      Email is required
                    </small>
                  )}
                </div>
                {!isPublic && (
                  <>
                    <input
                      type="checkbox"
                      checked={
                        modalForm?.requiredFields?.includes("email") || false
                      }
                      onChange={(e) =>
                        setModalForm((f) => ({
                          ...f,
                          requiredFields: e.target.checked
                            ? [...(f.requiredFields || []), "email"]
                            : (f.requiredFields || []).filter(
                                (field) => field !== "email"
                              ),
                        }))
                      }
                      style={{
                        width: 18,
                        height: 18,
                        cursor: "pointer",
                      }}
                    />
                    <input
                      type="checkbox"
                      checked={
                        modalForm?.removedFields?.includes("email") || false
                      }
                      onChange={(e) =>
                        setModalForm((f) => ({
                          ...f,
                          removedFields: e.target.checked
                            ? [...(f.removedFields || []), "email"]
                            : (f.removedFields || []).filter(
                                (field) => field !== "email"
                              ),
                        }))
                      }
                      style={{
                        width: 18,
                        height: 18,
                        cursor: "pointer",
                      }}
                    />
                  </>
                )}
              </div>
            </>
          )}

          {/* Number Field */}
          {!(isPublic && modalForm?.removedFields?.includes("number")) && (
            <>
              <label className="form-label">Number</label>

              <div className="form-group form-widget-field">
                <div style={{ flex: 1 }}>
                  <input
                    type="number"
                    className="form-control"
                    value={modalForm?.number || ""}
                    onChange={(e) => {
                      setModalForm((f) => ({
                        ...f,
                        number: e.target.value,
                      }));
                      setFieldErrors((prev) => ({ ...prev, number: false }));
                    }}
                    disabled={!isPublic}
                    placeholder="Enter a number"
                    style={{
                      borderColor: fieldErrors?.number ? "#dc3545" : "",
                      borderRadius:
                        typeof userData?.formTheme?.borderRadius === "number"
                          ? userData?.formTheme.borderRadius
                          : undefined,
                    }}
                  />
                  {isPublic && fieldErrors?.number && (
                    <small
                      style={{
                        color: "#dc3545",
                        display: "block",
                        marginTop: 4,
                      }}
                    >
                      Number is required
                    </small>
                  )}
                </div>
                {!isPublic && (
                  <>
                    <input
                      type="checkbox"
                      checked={
                        modalForm?.requiredFields?.includes("number") || false
                      }
                      onChange={(e) =>
                        setModalForm((f) => ({
                          ...f,
                          requiredFields: e.target.checked
                            ? [...(f.requiredFields || []), "number"]
                            : (f.requiredFields || []).filter(
                                (field) => field !== "number"
                              ),
                        }))
                      }
                      style={{
                        width: 18,
                        height: 18,
                        cursor: "pointer",
                      }}
                    />
                    <input
                      type="checkbox"
                      checked={
                        modalForm?.removedFields?.includes("number") || false
                      }
                      onChange={(e) =>
                        setModalForm((f) => ({
                          ...f,
                          removedFields: e.target.checked
                            ? [...(f.removedFields || []), "number"]
                            : (f.removedFields || []).filter(
                                (field) => field !== "number"
                              ),
                        }))
                      }
                      style={{
                        width: 18,
                        height: 18,
                        cursor: "pointer",
                      }}
                    />
                  </>
                )}
              </div>
            </>
          )}

          {/* Date of Birth Field */}
          {!(isPublic && modalForm?.removedFields?.includes("dob")) && (
            <>
              <label className="form-label">Date of birth</label>

              <div className="form-group form-widget-field">
                <div style={{ flex: 1 }}>
                  <input
                    type="date"
                    className="form-control"
                    value={modalForm?.dob || ""}
                    onChange={(e) => {
                      setModalForm((f) => ({ ...f, dob: e.target.value }));
                      setFieldErrors((prev) => ({ ...prev, dob: false }));
                    }}
                    disabled={!isPublic}
                    style={{
                      borderColor: fieldErrors?.dob ? "#dc3545" : "",
                      borderRadius:
                        typeof userData?.formTheme?.borderRadius === "number"
                          ? userData?.formTheme.borderRadius
                          : undefined,
                    }}
                  />
                  {isPublic && fieldErrors?.dob && (
                    <small
                      style={{
                        color: "#dc3545",
                        display: "block",
                        marginTop: 4,
                      }}
                    >
                      Date of birth is required
                    </small>
                  )}
                </div>
                {!isPublic && (
                  <>
                    <input
                      type="checkbox"
                      checked={
                        modalForm?.requiredFields?.includes("dob") || false
                      }
                      onChange={(e) =>
                        setModalForm((f) => ({
                          ...f,
                          requiredFields: e.target.checked
                            ? [...(f.requiredFields || []), "dob"]
                            : (f.requiredFields || []).filter(
                                (field) => field !== "dob"
                              ),
                        }))
                      }
                      style={{
                        width: 18,
                        height: 18,
                        cursor: "pointer",
                      }}
                    />
                    <input
                      type="checkbox"
                      checked={
                        modalForm?.removedFields?.includes("dob") || false
                      }
                      onChange={(e) =>
                        setModalForm((f) => ({
                          ...f,
                          removedFields: e.target.checked
                            ? [...(f.removedFields || []), "dob"]
                            : (f.removedFields || []).filter(
                                (field) => field !== "dob"
                              ),
                        }))
                      }
                      style={{
                        width: 18,
                        height: 18,
                        cursor: "pointer",
                      }}
                    />
                  </>
                )}
              </div>
            </>
          )}

          {/* Country Field */}
          {!(isPublic && modalForm?.removedFields?.includes("country")) && (
            <>
              <label className="form-label">Country</label>

              <div className="form-group form-widget-field">
                <div style={{ flex: 1 }}>
                  <select
                    className="form-control"
                    value={modalForm?.country || ""}
                    onChange={(e) => {
                      setModalForm((f) => ({
                        ...f,
                        country: e.target.value,
                      }));
                      setFieldErrors((prev) => ({ ...prev, country: false }));
                    }}
                    disabled={!isPublic}
                    style={{
                      borderColor: fieldErrors?.country ? "#dc3545" : "",
                      borderRadius:
                        typeof userData?.formTheme?.borderRadius === "number"
                          ? userData?.formTheme.borderRadius
                          : undefined,
                    }}
                  >
                    <option value="">Select a country</option>
                    {COUNTRY_LIST?.map((c) => (
                      <option key={c.code} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  {isPublic && fieldErrors?.country && (
                    <small
                      style={{
                        color: "#dc3545",
                        display: "block",
                        marginTop: 4,
                      }}
                    >
                      Country is required
                    </small>
                  )}
                </div>
                {!isPublic && (
                  <>
                    <input
                      type="checkbox"
                      checked={
                        modalForm?.requiredFields?.includes("country") || false
                      }
                      onChange={(e) =>
                        setModalForm((f) => ({
                          ...f,
                          requiredFields: e.target.checked
                            ? [...(f.requiredFields || []), "country"]
                            : (f.requiredFields || []).filter(
                                (field) => field !== "country"
                              ),
                        }))
                      }
                      style={{
                        width: 18,
                        height: 18,
                        cursor: "pointer",
                      }}
                    />
                    <input
                      type="checkbox"
                      checked={
                        modalForm?.removedFields?.includes("country") || false
                      }
                      onChange={(e) =>
                        setModalForm((f) => ({
                          ...f,
                          removedFields: e.target.checked
                            ? [...(f.removedFields || []), "country"]
                            : (f.removedFields || []).filter(
                                (field) => field !== "country"
                              ),
                        }))
                      }
                      style={{
                        width: 18,
                        height: 18,
                        cursor: "pointer",
                      }}
                    />
                  </>
                )}
              </div>
            </>
          )}
        </div>

        <div style={{ marginTop: 12 }}>
          {isPublic ? (
            <button
              className="btn btn-primary"
              onClick={handleSubmitPublicForm}
              disabled={loading}
            >
              {loading ? "Submitting..." : "Submit"}
            </button>
          ) : (
            <button
              className="btn btn-primary"
              onClick={handleSaveFormFields}
              disabled={loading}
            >
              {loading ? "Saving..." : "Save"}
            </button>
          )}
        </div>
      </>
    </div>
  );
};

export default FormWidgetModal;
